import { useEffect, useRef, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ManekiNeko } from "@/components/ManekiNeko";
import { useLang } from "@/lib/i18n";
import { Link } from "react-router-dom";
import { MessageSquare, X, Send, Loader2, FileText, RotateCcw, Star, CheckCircle2, Moon, Paperclip } from "lucide-react";

const KEY = "grcom_webchat";

function StartForm({ onStarted }) {
  const { t, lang } = useLang();
  const [f, setF] = useState({ name: "", contact: "", company: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try { const { data } = await api.post("/public/chat/start", { ...f, lang }); onStarted(data); }
    catch (ex) { setErr(formatApiErrorDetail(ex.response?.data?.detail) || t("Gagal memulai chat")); }
    finally { setBusy(false); }
  };
  const input = "w-full rounded-md border border-border bg-secondary px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40";
  return (
    <form onSubmit={submit} className="p-4 space-y-3" data-testid="webchat-start-form">
      <p className="text-sm text-muted-foreground">{t("Halo! Isi data singkat agar Nadia dapat mengirimkan penawaran kepada Anda.")}</p>
      <input data-testid="webchat-name" required placeholder={t("Nama Anda *")} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={input} />
      <input data-testid="webchat-contact" required placeholder={t("Email atau No. WhatsApp *")} value={f.contact} onChange={(e) => setF({ ...f, contact: e.target.value })} className={input} />
      <input data-testid="webchat-company" placeholder={t("Perusahaan (opsional)")} value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} className={input} />
      {err && <p className="text-xs text-red-600" data-testid="webchat-start-error">{err}</p>}
      <button type="submit" disabled={busy} data-testid="webchat-start-btn" className="w-full rounded-md bg-primary text-primary-foreground py-2.5 text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-60">
        {busy ? t("Menghubungkan…") : t("Mulai Chat")}
      </button>
    </form>
  );
}

function RatingPanel({ session, done, onBack, onReset }) {
  const { t } = useLang();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [sent, setSent] = useState(done);
  const [err, setErr] = useState("");
  const submit = async () => {
    setErr("");
    try { await api.post(`/public/chat/${session.case_id}/rating`, { token: session.token, rating, comment }); setSent(true); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || t("Gagal mengirim rating")); }
  };
  if (sent) return (
    <div className="border-t border-border p-4 text-center space-y-3" data-testid="webchat-rating-thanks">
      <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto" />
      <p className="text-sm font-medium">{t("Terima kasih atas penilaian Anda!")}</p>
      <button onClick={onReset} data-testid="webchat-new-chat-btn" className="w-full rounded-md bg-primary text-primary-foreground py-2 text-sm font-medium">{t("Mulai chat baru")}</button>
    </div>
  );
  return (
    <div className="border-t border-border p-4 space-y-3" data-testid="webchat-rating-panel">
      <p className="text-sm font-medium text-center">{t("Bagaimana pelayanan Nadia hari ini?")}</p>
      <div className="flex justify-center gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} onClick={() => setRating(n)} data-testid={`webchat-star-${n}`} aria-label={`${n} bintang`}>
            <Star className={`w-8 h-8 transition-colors ${n <= rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40"}`} />
          </button>
        ))}
      </div>
      <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} placeholder={t("Saran atau komentar (opsional)")} data-testid="webchat-rating-comment"
        className="w-full rounded-md border border-border bg-secondary px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40" />
      {err && <p className="text-xs text-red-600">{err}</p>}
      <button onClick={submit} disabled={!rating} data-testid="webchat-rating-submit" className="w-full rounded-md bg-primary text-primary-foreground py-2 text-sm font-medium disabled:opacity-50">{t("Kirim Rating")}</button>
      {onBack && <button onClick={onBack} data-testid="webchat-rating-back" className="w-full text-xs text-muted-foreground hover:text-foreground">{t("Kembali ke percakapan")}</button>}
    </div>
  );
}

function ChatBody({ session, onReset }) {
  const { t } = useLang();
  const [data, setData] = useState({ messages: [], processing: true });
  const [text, setText] = useState("");
  const [err, setErr] = useState("");
  const [rate, setRate] = useState(false);
  const endRef = useRef(null);
  const fileRef = useRef(null);
  const fetchIt = useCallback(async () => {
    try { const { data: d } = await api.get(`/public/chat/${session.case_id}`, { params: { token: session.token } }); setData(d); }
    catch (e) { if (e.response?.status === 404) onReset(); }
  }, [session, onReset]);
  useEffect(() => { fetchIt(); }, [fetchIt]);
  useEffect(() => {
    if (!data.processing) return undefined;
    const t = setInterval(fetchIt, 2500);
    return () => clearInterval(t);
  }, [data.processing, fetchIt]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [data.messages.length, data.processing]);

  const send = async () => {
    const msg = text.trim();
    if (!msg || data.processing) return;
    setText(""); setErr("");
    setData((d) => ({ ...d, processing: true, messages: [...d.messages, { role: "user", content: msg }] }));
    try { await api.post(`/public/chat/${session.case_id}/message`, { token: session.token, message: msg }); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); fetchIt(); }
  };
  const sendFile = async (file) => {
    if (!file || data.processing) return;
    if (file.size > 15 * 1024 * 1024) { setErr(t("File terlalu besar (maks 15 MB)")); return; }
    const msg = text.trim();
    setText(""); setErr("");
    setData((d) => ({ ...d, processing: true, messages: [...d.messages, { role: "user", content: `${msg ? msg + "\n" : ""}[File] ${file.name}` }] }));
    const fd = new FormData();
    fd.append("token", session.token); fd.append("message", msg); fd.append("file", file);
    try { await api.post(`/public/chat/${session.case_id}/attachment`, fd); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); fetchIt(); }
  };
  const linkify = (s) => s.split(/(https?:\/\/\S+)/g).map((p, i) => (/^https?:\/\//.test(p)
    ? <a key={i} href={p} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline font-medium"><FileText className="w-3 h-3" />{t("Buka dokumen")}</a> : p));

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-3 space-y-3" data-testid="webchat-messages">
        {data.messages.filter((m, idx) => !(idx === 0 && m.role === "user")).map((m, i) => (
          <div key={`${m.ts || "local"}-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap break-words ${m.role === "user" ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-secondary border border-border rounded-bl-sm"}`}>
              {m.role !== "user" && m.agent && <div className="text-[10px] font-bold uppercase tracking-wider text-accent mb-0.5">{m.agent}</div>}
              {linkify(m.content)}
            </div>
          </div>
        ))}
        {data.processing && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="webchat-typing"><Loader2 className="w-3.5 h-3.5 animate-spin" /> {t("Tim kami sedang mengetik…")}</div>
        )}
        <div ref={endRef} />
      </div>
      {err && <p className="px-3 text-xs text-red-600" data-testid="webchat-error">{err}</p>}
      {data.status === "closed" || rate ? (
        <RatingPanel session={session} done={!!data.rating} onReset={onReset} onBack={data.status === "closed" ? null : () => setRate(false)} />
      ) : (
        <>
          <div className="border-t border-border p-2 flex gap-2">
            <input ref={fileRef} type="file" className="hidden" data-testid="webchat-file-input"
              accept=".pdf,.jpg,.jpeg,.png,.webp,.xlsx,.xlsm,.xls,.csv,.docx,.doc,.pptx,.ppt,.txt"
              onChange={(e) => { sendFile(e.target.files?.[0]); e.target.value = ""; }} />
            <button onClick={() => fileRef.current?.click()} disabled={data.processing} data-testid="webchat-attach-btn" aria-label={t("Lampirkan file")}
              className="w-10 h-10 rounded-full border border-border text-muted-foreground flex items-center justify-center hover:text-foreground disabled:opacity-50"><Paperclip className="w-4 h-4" /></button>
            <input data-testid="webchat-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
              disabled={data.processing} placeholder={data.processing ? t("Menunggu balasan…") : t("Tulis pesan…")} className="flex-1 rounded-full border border-border bg-secondary px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40" />
            <button onClick={send} disabled={data.processing || !text.trim()} data-testid="webchat-send-btn" className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center disabled:opacity-50"><Send className="w-4 h-4" /></button>
          </div>
          <button onClick={() => setRate(true)} className="text-[11px] text-muted-foreground py-1.5 hover:text-foreground flex items-center justify-center gap-1" data-testid="webchat-finish-btn"><RotateCcw className="w-3 h-3" /> {t("Selesai & beri rating")}</button>
        </>
      )}
    </div>
  );
}

export function ChatLauncher() {
  const { t } = useLang();
  const [menu, setMenu] = useState(false);
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState(() => { try { return JSON.parse(localStorage.getItem(KEY)); } catch { return null; } });
  const start = (s) => { localStorage.setItem(KEY, JSON.stringify(s)); setSession(s); };
  const reset = useCallback(() => { localStorage.removeItem(KEY); setSession(null); }, []);
  const opt = "flex items-center gap-3 w-full text-left px-4 py-3 rounded-lg hover:bg-secondary transition-colors";
  const [online, setOnline] = useState(true);
  useEffect(() => {
    const check = () => api.get("/public/chat/status").then((r) => setOnline(r.data.online)).catch(() => {});
    check();
    const iv = setInterval(check, 60000);
    return () => clearInterval(iv);
  }, []);

  return (
    <>
      {open && (
        <div className="fixed z-50 bottom-24 right-4 sm:right-6 w-[calc(100vw-2rem)] sm:w-96 h-[70vh] max-h-[600px] bg-card border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden" data-testid="webchat-panel">
          <div className="bg-primary text-primary-foreground px-4 py-3 flex items-center justify-between">
            <div><div className="font-semibold text-sm">Nadia · CV GRCom</div><div className="text-[11px] opacity-80">{t("CS & Intake — biasanya membalas dalam 1 menit")}</div></div>
            <button onClick={() => setOpen(false)} data-testid="webchat-close-btn" aria-label="Tutup"><X className="w-5 h-5" /></button>
          </div>
          <div className="flex-1 min-h-0">{session ? <ChatBody session={session} onReset={reset} /> : <StartForm onStarted={start} />}</div>
        </div>
      )}
      {menu && !open && (
        <div className="fixed z-50 bottom-24 right-4 sm:right-6 w-72 bg-card border border-border rounded-2xl shadow-2xl p-2" data-testid="chat-launcher-menu">
          {online && <div className="px-4 pt-2 pb-1 text-xs text-muted-foreground">{t("Hubungi Nadia lewat:")}</div>}
          {(online || session) && (
            <button className={opt} onClick={() => { setMenu(false); setOpen(true); }} data-testid="chat-option-web">
              <MessageSquare className="w-5 h-5 text-primary" /><span><span className="block text-sm font-medium">{t("Support Team")}</span><span className="block text-[11px] text-muted-foreground">{session ? t("Lanjutkan percakapan Anda") : t("Langsung di halaman ini")}</span></span>
            </button>
          )}
          {!online && (
            <div className="px-4 py-3 space-y-3" data-testid="chat-offline-info">
              <div className="flex items-start gap-3">
                <Moon className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
                <div>
                  <div className="text-sm font-medium">{t("Nadia sedang offline")}</div>
                  <div className="text-[11px] text-muted-foreground">{t("Chat aktif Senin–Jumat, 08.00–17.00 WIB.")}</div>
                </div>
              </div>
              <Link to="/request" data-testid="chat-offline-request-link" className="block text-center rounded-md bg-primary text-primary-foreground py-2 text-sm font-medium hover:opacity-90 transition-opacity">
                {t("Kirim permintaan penawaran")}
              </Link>
            </div>
          )}
        </div>
      )}
      {open || menu ? (
        <button onClick={() => { setOpen(false); setMenu(false); }} data-testid="chat-launcher-btn" aria-label="Tutup chat"
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-lg hover:-translate-y-0.5 transition-transform">
          <X className="w-6 h-6" />
        </button>
      ) : (
        <ManekiNeko onClick={() => setMenu(true)} online={online} />
      )}
    </>
  );
}
